import { useEffect, useState } from 'react';
import * as api from '../api/client.js';
import { CreateRoundDialog } from './CreateRoundDialog.js';
import { JoinRoundDialog } from './JoinRoundDialog.js';

const TEMPO_LABELS = {
  blitz: { name: 'ברק', desc: '30 יום אמת', color: 'var(--accent-danger)' },
  normal: { name: 'רגיל', desc: '3 חודשים', color: 'var(--accent-primary)' },
  slow: { name: 'איטי', desc: '6 חודשים', color: 'var(--accent-success)' },
};

const STATUS_LABELS = {
  waiting: 'ממתין לשחקנים',
  running: 'רץ',
  finished: 'הסתיים',
  cancelled: 'בוטל',
};

export function LobbyScreen({
  onEnterRound,
  onLogout,
}: {
  onEnterRound: (roundId: string) => void;
  onLogout: () => void;
}) {
  const [rounds, setRounds] = useState<api.RoundSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [joinRoundId, setJoinRoundId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const account = api.getAccount();

  const refresh = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listRounds();
      setRounds(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 15_000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = (): void => {
    api.logout();
    onLogout();
  };

  const handleRoundCreated = (round: api.RoundSummary): void => {
    setShowCreate(false);
    setJoinRoundId(round.id);
    void refresh();
  };

  return (
    <div className="lobby">
      <header className="lobby-header">
        <h1>חצרות</h1>
        <div className="account-info">
          <span>שלום, {account?.displayName ?? ''}</span>
          <button onClick={handleLogout}>יציאה</button>
        </div>
      </header>

      <div className="lobby-actions">
        <button className="primary" onClick={() => setShowCreate(true)}>
          + משחק חדש
        </button>
        <button onClick={() => void refresh()} disabled={loading}>
          🔄 רענן
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="round-grid">
        {loading && rounds.length === 0 ? (
          <div className="empty">טוען...</div>
        ) : rounds.length === 0 ? (
          <div className="empty">
            אין משחקים פתוחים כרגע. צור אחד חדש והזמן חברים.
          </div>
        ) : (
          rounds.map((round) => {
            const tempo = TEMPO_LABELS[round.tempo];
            return (
              <div key={round.id} className="round-card">
                <div className="round-header">
                  <h3>{round.name}</h3>
                  <span className="badge" style={{ background: tempo.color }}>
                    {tempo.name}
                  </span>
                </div>
                <div className="round-meta">
                  <div>
                    <strong>סטטוס:</strong> {STATUS_LABELS[round.status]}
                  </div>
                  <div>
                    <strong>משך:</strong> {tempo.desc}
                  </div>
                  <div>
                    <strong>שנה בעולם:</strong>{' '}
                    {Math.floor(round.current_tick / 360)} / 100
                  </div>
                </div>
                <div className="round-actions">
                  {round.status === 'waiting' ? (
                    <button
                      className="primary"
                      onClick={() => setJoinRoundId(round.id)}
                    >
                      הצטרף
                    </button>
                  ) : round.status === 'running' ? (
                    <button
                      className="primary"
                      onClick={() => onEnterRound(round.id)}
                    >
                      כניסה
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showCreate && (
        <CreateRoundDialog
          onClose={() => setShowCreate(false)}
          onCreated={handleRoundCreated}
        />
      )}
      {joinRoundId && (
        <JoinRoundDialog
          roundId={joinRoundId}
          onClose={() => setJoinRoundId(null)}
          onJoined={() => {
            setJoinRoundId(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
